<script setup>
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import DefaultWhiteCard from "@/components/global-themed/ui/sections/DefaultWhiteCard.vue";
import HeadingTitle from "@/components/website/v2/ui/HeadingTitle.vue";
import KitIcon from "@/components/approval-center/partials/kit/KitIcon.vue";
import SectionText from "@/components/website/v2/ui/SectionText.vue";
import SectionBorderedWrapper from "@/components/website/v2/ui/SectionBorderedWrapper.vue";
import SectionSubText from "@/components/website/v2/ui/SectionSubText.vue";
import KitShadow from "@/components/website/v2/ui/KitShadow.vue";
import CustomMultiselect from "@/components/global-themed/CustomMultiselect.vue";
import {computed, watch} from "vue";
import {useQuery} from "@tanstack/vue-query";
import axios from "axios";
import {previousStep, useCreateWebsite} from "@/composables/website/v2/useCreateWebsite";

const {
  industry,
  country,
  region,
  location,
  countriesQuery,
  industriesQuery,
  createWebsiteMutation,
} = useCreateWebsite();

const {
  data: industriesData,
  isLoading: isIndustriesLoading
} = industriesQuery;

const {
  data: countriesData,
  isLoading: isCountryLoading
} = countriesQuery;

const {
  data: regionsData,
  refetch: refetchRegions,
  isLoading: isRegionsLoading,
} = useQuery({
  queryKey: ['regions', country],
  queryFn: async () => {
    if (!country.value) return {};

    const {data} = await axios.get('/ajax/v2/website/regions', {
      params: {parent: country.value}
    });

    return data;
  },
  enabled: computed(() => !!country.value)
});

const {
  data: locationsData,
  refetch: refetchLocations,
  isLoading: isLocationsLoading
} = useQuery({
  queryKey: ['locations', region],
  queryFn: async () => {
    if (!region.value) return {};

    const {data} = await axios.get('/ajax/v2/website/regions', {
      params: {parent: region.value}
    });

    return data;
  },
  enabled: computed(() => !!region.value)
});

watch(country, (newValue) => {
  if (newValue) {
    refetchRegions();
    region.value = '';
    location.value = '';
  }
});

watch(region, (newValue) => {
  if (newValue) {
    refetchLocations();
    location.value = '';
  }
});

const isFormValid = computed(() => {
  return !!industry.value && !!country.value;
});

const isLoading = computed(() => isIndustriesLoading.value || isCountryLoading.value || isRegionsLoading.value || isLocationsLoading.value);

const {mutate: handleCreate, isPending} = createWebsiteMutation;
</script>

<template>
  <DefaultWhiteCard>
    <div class="tw-flex tw-flex-col tw-gap-y-16 tw-items-center">
      <HeadingTitle>Add your location and industry 📍</HeadingTitle>
      <div class="tw-flex tw-flex-col tw-gap-y-8 tw-items-center">
        <div class="tw-flex tw-flex-col tw-items-end tw-gap-4">
          <KitIcon
            state="thinking"
            class="tw-w-44"
          />
          <KitShadow class="tw-w-[142px]" />
        </div>
        <SectionBorderedWrapper>
          <SectionText>Next, please tell me your industry and the business location.</SectionText>

          <div class="tw-space-y-5">
            <SectionSubText>Your website's industry:</SectionSubText>
            <CustomMultiselect
              v-model="industry"
              class="tw-text-left"
              :close-on-select="true"
              :options="industriesData || {}"
              :searchable="true"
              :can-deselect="false"
              :disabled="isIndustriesLoading"
            />
          </div>

          <div class="tw-space-y-5">
            <SectionSubText>Primary location of business:</SectionSubText>
            <CustomMultiselect
              v-model="country"
              class="tw-text-left"
              :close-on-select="true"
              :options="countriesData || {}"
              :searchable="true"
              :can-deselect="false"
              :disabled="isCountryLoading"
            />
          </div>

          <div
            v-if="country && regionsData && Object.keys(regionsData).length > 0"
            class="tw-space-y-5"
          >
            <SectionSubText>Region of business <span class="tw-italic">(Optional)</span>:</SectionSubText>
            <CustomMultiselect
              v-model="region"
              class="tw-text-left"
              :close-on-select="true"
              :options="regionsData"
              :searchable="true"
              groups
            />
          </div>

          <div
            v-if="region && locationsData && Object.keys(locationsData).length > 0"
            class="tw-space-y-5"
          >
            <SectionSubText>Location of business <span class="tw-italic">(Optional)</span>:</SectionSubText>
            <CustomMultiselect
              v-model="location"
              class="tw-text-left"
              :close-on-select="true"
              :options="locationsData"
              :searchable="true"
              groups
            />
          </div>

          <div class="tw-flex tw-justify-between tw-items-center">
            <span
              class="tw-font-base tw-underline tw-text-neutral-900 tw-cursor-pointer"
              @click="previousStep()"
            >
              Back
            </span>

            <DefaultButton
              v-tooltip="{
                content: 'Please select location and industry to continue',
                placement: 'bottom',
                theme: 'dark-blue-tooltip',
                disabled: isFormValid
              }"
              class="tw-self-end"
              :disabled="!isFormValid || isLoading || isPending"
              @click="handleCreate"
            >
              Continue
            </DefaultButton>
          </div>
        </SectionBorderedWrapper>
      </div>
    </div>
  </DefaultWhiteCard>
</template>
